import { useState } from "react";
import {
  Tag,
  Plus,
  MoreHorizontal,
  Pencil,
  Trash2,
  LayoutGrid,
  List,
  Lock,
  Sparkles,
} from "lucide-react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { Badge } from "@/components/ui/badge";
import { Button, FloatingActionButton } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Skeleton } from "@/components/ui/skeleton";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { useIsMobile } from "@/hooks/useMobileUtils";
import {
  useTags,
  useCreateTag,
  useUpdateTag,
  useDeleteTag,
} from "@/hooks/useQueries";
import { cn } from "@/lib/utils";
import { useAuthStore } from "@/store/auth.store";

// Import TanStack Query hooks

const TAG_COLORS = [
  "#6366f1",
  "#8b5cf6",
  "#ec4899",
  "#ef4444",
  "#f97316",
  "#eab308",
  "#22c55e",
  "#14b8a6",
  "#3b82f6",
  "#64748b",
];

// Color picker component
const ColorPicker = ({ value, onChange }) => (
  <div className="flex flex-wrap gap-2">
    {TAG_COLORS.map((color) => (
      <button
        key={color}
        aria-label="Tag Colour"
        type="button"
        onClick={() => onChange(color)}
        className={cn(
          "h-8 w-8 rounded-full border-2 transition-transform hover:scale-110",
          value === color
            ? "border-foreground scale-110"
            : "border-transparent",
        )}
        style={{ backgroundColor: color }}
      />
    ))}
  </div>
);

// Tag card component for grid view
const TagCard = ({ tag, onEdit, onDelete, onClick }) => (
  <Card
    className="group cursor-pointer hover:shadow-md transition-shadow overflow-hidden"
    onClick={onClick}
  >
    <div
      className="h-32 flex items-center justify-center relative"
      style={{ backgroundColor: `${tag.color}15` }}
    >
      <div
        className="flex h-16 w-16 items-center justify-center rounded-xl"
        style={{ backgroundColor: `${tag.color}25` }}
      >
        <Tag className="h-8 w-8" style={{ color: tag.color }} />
      </div>

      <div className="absolute top-2 right-2 z-10">
        <DropdownMenu>
          <DropdownMenuTrigger asChild onClick={(e) => e.stopPropagation()}>
            <Button variant="secondary" size="icon" className="h-8 w-8">
              <MoreHorizontal className="h-4 w-4" />
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end">
            <DropdownMenuItem
              onClick={(e) => {
                e.stopPropagation();
                onEdit(tag);
              }}
            >
              <Pencil className="mr-2 h-4 w-4" />
              Edit
            </DropdownMenuItem>
            <DropdownMenuSeparator />
            <DropdownMenuItem
              onClick={(e) => {
                e.stopPropagation();
                onDelete(tag);
              }}
              className="text-destructive focus:text-destructive"
            >
              <Trash2 className="mr-2 h-4 w-4" />
              Delete
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      </div>
    </div>

    <CardContent className="p-4">
      <h3 className="font-medium truncate mb-1">{tag.name}</h3>
      <div className="flex items-center justify-between">
        <p className="text-sm text-muted-foreground">
          {tag.envelopeCount || 0} envelope
          {(tag.envelopeCount || 0) !== 1 ? "s" : ""}
        </p>
        <Badge
          variant="outline"
          className="gap-1"
          style={{
            borderColor: tag.color,
            backgroundColor: `${tag.color}20`,
            color: tag.color,
          }}
        >
          <Tag className="h-3 w-3" />
          {tag.name}
        </Badge>
      </div>
    </CardContent>
  </Card>
);

// Tag list row component
const TagListRow = ({ tag, onEdit, onDelete, onClick }) => (
  <TableRow className="cursor-pointer" onClick={onClick}>
    <TableCell>
      <div className="flex items-center gap-3">
        <div
          className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg"
          style={{ backgroundColor: `${tag.color}20` }}
        >
          <Tag className="h-4 w-4" style={{ color: tag.color }} />
        </div>
        <Badge
          variant="outline"
          className="gap-1"
          style={{
            borderColor: tag.color,
            backgroundColor: `${tag.color}20`,
            color: tag.color,
          }}
        >
          <Tag className="h-3 w-3" />
          {tag.name}
        </Badge>
      </div>
    </TableCell>
    <TableCell className="text-muted-foreground">
      {tag.envelopeCount || 0} envelopes
    </TableCell>
    <TableCell>
      <DropdownMenu>
        <DropdownMenuTrigger asChild onClick={(e) => e.stopPropagation()}>
          <Button variant="ghost" size="icon" className="h-8 w-8">
            <MoreHorizontal className="h-4 w-4" />
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end">
          <DropdownMenuItem
            onClick={(e) => {
              e.stopPropagation();
              onEdit(tag);
            }}
          >
            <Pencil className="mr-2 h-4 w-4" />
            Edit
          </DropdownMenuItem>
          <DropdownMenuSeparator />
          <DropdownMenuItem
            onClick={(e) => {
              e.stopPropagation();
              onDelete(tag);
            }}
            className="text-destructive focus:text-destructive"
          >
            <Trash2 className="mr-2 h-4 w-4" />
            Delete
          </DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>
    </TableCell>
  </TableRow>
);

// Loading skeleton
const TagsSkeleton = () => (
  <div className="space-y-6">
    <div className="space-y-2">
      <Skeleton className="h-8 w-48" />
      <Skeleton className="h-4 w-64" />
    </div>
    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
      {[1, 2, 3, 4].map((i) => (
        <div key={i} className="rounded-lg border bg-card overflow-hidden">
          <Skeleton className="h-32 rounded-none" />
          <div className="p-4 space-y-2">
            <Skeleton className="h-5 w-3/4" />
            <Skeleton className="h-4 w-1/2" />
          </div>
        </div>
      ))}
    </div>
  </div>
);

// Empty state component
const EmptyState = ({ onCreateClick }) => {
  const { t } = useTranslation();

  return (
    <Card>
      <CardContent className="flex flex-col items-center justify-center py-12 text-center">
        <div className="flex h-16 w-16 items-center justify-center rounded-full bg-muted mb-4">
          <Tag className="h-8 w-8 text-muted-foreground" />
        </div>
        <h3 className="text-lg font-semibold mb-1">{t("tags.noTags")}</h3>
        <p className="text-sm text-muted-foreground mb-4 max-w-sm">
          {t("tags.noTagsDesc")}
        </p>
        <Button onClick={onCreateClick}>
          <Plus className="mr-2 h-4 w-4" />
          {t("tags.createTag")}
        </Button>
      </CardContent>
    </Card>
  );
};

// Upgrade Required component
const UpgradeRequired = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();

  return (
    <div className="space-y-6">
      {/* Page header */}
      <div>
        <h1 className="text-2xl font-bold font-heading tracking-tight">
          {t("tags.title")}
        </h1>
        <p className="text-muted-foreground">{t("tags.subtitle")}</p>
      </div>

      {/* Upgrade Required Card */}
      <Card className="border-dashed">
        <CardContent className="flex flex-col items-center justify-center py-16 text-center">
          <div className="flex h-20 w-20 items-center justify-center rounded-full bg-primary/10 mb-6">
            <Lock className="h-10 w-10 text-primary" />
          </div>
          <h2 className="text-2xl font-semibold mb-2">
            {t("upgrade.featureNotAvailable", "Feature Not Available")}
          </h2>
          <p className="text-muted-foreground mb-6 max-w-md">
            {t(
              "upgrade.tagsUpgradeDesc",
              "Tags help you categorize and filter your documents with custom labels. Upgrade your plan to unlock this powerful organisation feature.",
            )}
          </p>
          <div className="flex flex-col sm:flex-row gap-3">
            <Button onClick={() => navigate("/settings/billing")}>
              <Sparkles className="mr-2 h-4 w-4" />
              {t("upgrade.upgradePlan", "Upgrade Plan")}
            </Button>
            <Button variant="outline" onClick={() => navigate("/envelopes")}>
              {t("common.backToEnvelopes", "Back to Envelopes")}
            </Button>
          </div>

          {/* Feature highlights */}
          <div className="mt-8 pt-8 border-t w-full max-w-lg">
            <div className="w-full flex items-center justify-center gap-2 mb-4">
              <Tag className="h-3.5 w-3.5 text-primary" />
              <h3 className="text-sm font-medium text-muted-foreground">
                {t("upgrade.withTags", "With Tags you can:")}
              </h3>
            </div>
            <ul className="text-sm text-muted-foreground space-y-1 flex items-center justify-center flex-col">
              <li>
                {t("upgrade.tagsFeature1", "Add multiple tags to any envelope")}
              </li>
              <li>
                {t(
                  "upgrade.tagsFeature2",
                  "Filter envelopes by tags for quick access",
                )}
              </li>
              <li>
                {t(
                  "upgrade.tagsFeature3",
                  "Color-code tags for visual organisation",
                )}
              </li>
            </ul>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export const Tags = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const isMobile = useIsMobile();

  // Get user subscription to check feature access
  const user = useAuthStore((state) => state.user);

  // Check if user has tags feature - must be explicitly true
  // undefined or false means no access
  const hasTagsFeature = user?.subscription?.features?.tags === true;

  // Local UI state only
  const [viewMode, setViewMode] = useState("grid");
  const [showCreateDialog, setShowCreateDialog] = useState(false);
  const [showEditDialog, setShowEditDialog] = useState(false);
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [selectedTag, setSelectedTag] = useState(null);
  const [tagName, setTagName] = useState("");
  const [tagColor, setTagColor] = useState(TAG_COLORS[0]);

  // ============================================
  // TanStack Query - Data fetching (only if feature enabled)
  // ============================================
  const { data: tags = [], isLoading } = useTags({
    enabled: hasTagsFeature,
  });

  // ============================================
  // TanStack Query - Mutations
  // ============================================
  const createTag = useCreateTag();
  const updateTag = useUpdateTag();
  const deleteTag = useDeleteTag();

  // ============================================
  // Feature Check - Show upgrade message if tags not available
  // ============================================
  if (!hasTagsFeature) {
    return <UpgradeRequired />;
  }

  // ============================================
  // Event Handlers
  // ============================================
  const resetForm = () => {
    setTagName("");
    setTagColor(TAG_COLORS[0]);
    setSelectedTag(null);
  };

  const handleCreateTag = (e) => {
    e.preventDefault();
    if (!tagName.trim()) return;

    createTag.mutate(
      { name: tagName, color: tagColor },
      {
        onSuccess: () => {
          setShowCreateDialog(false);
          resetForm();
        },
      },
    );
  };

  const handleEditTag = (e) => {
    e.preventDefault();
    if (!selectedTag || !tagName.trim()) return;

    updateTag.mutate(
      { id: selectedTag.id, data: { name: tagName, color: tagColor } },
      {
        onSuccess: () => {
          setShowEditDialog(false);
          resetForm();
        },
      },
    );
  };

  const handleDeleteTag = () => {
    if (!selectedTag) return;

    deleteTag.mutate(selectedTag.id, {
      onSuccess: () => {
        setShowDeleteDialog(false);
        resetForm();
      },
    });
  };

  const openEditDialog = (tag) => {
    setSelectedTag(tag);
    setTagName(tag.name);
    setTagColor(tag.color || TAG_COLORS[0]);
    setShowEditDialog(true);
  };

  const openDeleteDialog = (tag) => {
    setSelectedTag(tag);
    setShowDeleteDialog(true);
  };

  const handleTagClick = (tag) => {
    navigate(`/envelopes?tag=${tag.id}`);
  };

  const renderContent = () => {
    if (tags.length === 0) {
      return <EmptyState onCreateClick={() => setShowCreateDialog(true)} />;
    }
    return viewMode === "grid" ? (
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
        {tags.map((tag) => (
          <TagCard
            key={tag.id}
            tag={tag}
            onEdit={openEditDialog}
            onDelete={openDeleteDialog}
            onClick={() => handleTagClick(tag)}
          />
        ))}
      </div>
    ) : (
      <Card>
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>{t("common.name")}</TableHead>
              <TableHead>{t("common.envelopes")}</TableHead>
              <TableHead className="w-12.5" />
            </TableRow>
          </TableHeader>
          <TableBody>
            {tags.map((tag) => (
              <TagListRow
                key={tag.id}
                tag={tag}
                onEdit={openEditDialog}
                onDelete={openDeleteDialog}
                onClick={() => handleTagClick(tag)}
              />
            ))}
          </TableBody>
        </Table>
      </Card>
    );
  };

  // ============================================
  // Render
  // ============================================
  if (isLoading) {
    return <TagsSkeleton />;
  }

  return (
    <div className="space-y-6 pb-20 sm:pb-0">
      {/* Page header */}
      <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h1 className="text-2xl font-semibold tracking-tight">
            {t("tags.title")}
          </h1>
          <p className="text-muted-foreground">{t("tags.subtitle")}</p>
        </div>
        <div className="flex items-center gap-2">
          {/* View toggle */}
          <div className="flex items-center border rounded-lg p-1">
            <Button
              variant={viewMode === "grid" ? "secondary" : "ghost"}
              size="sm"
              className="h-8 px-2"
              onClick={() => setViewMode("grid")}
            >
              <LayoutGrid className="h-4 w-4" />
            </Button>
            <Button
              variant={viewMode === "list" ? "secondary" : "ghost"}
              size="sm"
              className="h-8 px-2"
              onClick={() => setViewMode("list")}
            >
              <List className="h-4 w-4" />
            </Button>
          </div>
          <Button
            onClick={() => setShowCreateDialog(true)}
            className="hidden sm:inline-flex"
          >
            <Plus className="mr-2 h-4 w-4" />
            {t("tags.newTag")}
          </Button>
        </div>
      </div>

      {/* Tags grid/list */}
      {renderContent()}

      {/* Mobile FAB */}
      {isMobile && (
        <FloatingActionButton
          icon={Plus}
          onClick={() => setShowCreateDialog(true)}
          label={t("tags.newTag")}
        />
      )}

      {/* Create Tag Dialog */}
      <Dialog open={showCreateDialog} onOpenChange={setShowCreateDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{t("tags.createTag")}</DialogTitle>
            <DialogDescription>{t("tags.createTagDesc")}</DialogDescription>
          </DialogHeader>
          <form onSubmit={handleCreateTag}>
            <div className="space-y-4 py-4">
              <div className="space-y-2">
                <Label htmlFor="tagName">{t("common.name")}</Label>
                <Input
                  id="tagName"
                  value={tagName}
                  onChange={(e) => setTagName(e.target.value)}
                  placeholder={t("tags.namePlaceholder")}
                />
              </div>
              <div className="space-y-2">
                <Label>{t("tags.colour")}</Label>
                <ColorPicker value={tagColor} onChange={setTagColor} />
              </div>
            </div>
            <DialogFooter>
              <Button
                type="button"
                variant="outline"
                onClick={() => setShowCreateDialog(false)}
                size={isMobile ? "mobile" : "default"}
              >
                {t("common.cancel")}
              </Button>
              <Button
                type="submit"
                disabled={createTag.isPending || !tagName.trim()}
                size={isMobile ? "mobile" : "default"}
              >
                {createTag.isPending ? t("common.loading") : t("common.create")}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Edit Tag Dialog */}
      <Dialog open={showEditDialog} onOpenChange={setShowEditDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{t("tags.editTag")}</DialogTitle>
            <DialogDescription>{t("tags.editTagDesc")}</DialogDescription>
          </DialogHeader>
          <form onSubmit={handleEditTag}>
            <div className="space-y-4 py-4">
              <div className="space-y-2">
                <Label htmlFor="editTagName">{t("common.name")}</Label>
                <Input
                  id="editTagName"
                  value={tagName}
                  onChange={(e) => setTagName(e.target.value)}
                  placeholder={t("tags.namePlaceholder")}
                />
              </div>
              <div className="space-y-2">
                <Label>{t("tags.color")}</Label>
                <ColorPicker value={tagColor} onChange={setTagColor} />
              </div>
            </div>
            <DialogFooter>
              <Button
                type="button"
                variant="outline"
                onClick={() => setShowEditDialog(false)}
                size={isMobile ? "mobile" : "default"}
              >
                {t("common.cancel")}
              </Button>
              <Button
                type="submit"
                disabled={updateTag.isPending || !tagName.trim()}
                size={isMobile ? "mobile" : "default"}
              >
                {updateTag.isPending ? t("common.loading") : t("common.save")}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog open={showDeleteDialog} onOpenChange={setShowDeleteDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{t("tags.deleteTag")}</DialogTitle>
            <DialogDescription>{t("tags.deleteTagDesc")}</DialogDescription>
          </DialogHeader>
          <div className="py-4">
            <div className="flex items-center gap-2">
              <Badge
                variant="outline"
                className="gap-1"
                style={{
                  borderColor: selectedTag?.color,
                  backgroundColor: `${selectedTag?.color}20`,
                  color: selectedTag?.color,
                }}
              >
                <Tag className="h-3 w-3" />
                {selectedTag?.name}
              </Badge>
            </div>
            <p className="text-sm text-muted-foreground mt-2">
              This tag will be removed from {selectedTag?.envelopeCount || 0}{" "}
              envelopes.
            </p>
          </div>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setShowDeleteDialog(false)}
              size={isMobile ? "mobile" : "default"}
            >
              {t("common.cancel")}
            </Button>
            <Button
              variant="destructive"
              onClick={handleDeleteTag}
              disabled={deleteTag.isPending}
              size={isMobile ? "mobile" : "default"}
            >
              {deleteTag.isPending ? t("common.loading") : t("common.delete")}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default Tags;
